import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis, ReferenceLine, CartesianGrid } from "recharts";

export default function PriceChart({ ticker, timeframe }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!ticker) return;
    let cancel = false;
    setLoading(true);
    api.get(`/api/scanner/quote/${ticker}?timeframe=${timeframe}`)
      .then((r) => { if (!cancel) setData(r.data); })
      .catch(() => {})
      .finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, [ticker, timeframe]);

  if (!ticker) {
    return (
      <div className="card-surface p-6 h-[340px] flex items-center justify-center" data-testid="chart-empty">
        <div className="text-white/40 text-sm label-mono">Select a ticker to view chart</div>
      </div>
    );
  }
  const candles = data?.candles || [];
  const chartData = candles.map((c, i) => ({ i, c: c.c, h: c.h, l: c.l, o: c.o }));
  const ind = data?.indicators;
  const change = candles.length >= 2 ? ((candles[candles.length - 1].c - candles[0].c) / candles[0].c) * 100 : 0;
  const isUp = change >= 0;
  const color = isUp ? "#00ff9c" : "#ff4d6d";

  return (
    <div className="card-surface p-5 fade-in glass-hover" data-testid={`chart-${ticker}`}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-baseline gap-3">
            <div className="text-2xl text-white font-semibold tracking-tight">{ticker}</div>
            <div className={`font-mono text-sm font-medium ${isUp ? "text-bull" : "text-bear"}`}>
              {isUp ? "+" : ""}{change.toFixed(2)}%
            </div>
          </div>
          <div className="label-mono mt-1">{timeframe} · {chartData.length} bars · {data?.data_source || "—"}</div>
        </div>
        <div className="font-mono text-3xl text-white tabular-nums tracking-tight">
          {data?.price?.toFixed(2) ?? "—"}
        </div>
      </div>
      <div style={{ height: 200 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 8, right: 4, left: 4, bottom: 0 }}>
            <defs>
              <linearGradient id={`grad-${ticker}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.5} />
                <stop offset="100%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="2 4" stroke="rgba(255,255,255,0.04)" vertical={false} />
            <XAxis dataKey="i" hide />
            <YAxis domain={["auto", "auto"]} hide />
            <Tooltip
              contentStyle={{ background: "rgba(12,12,18,0.95)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, fontFamily: "JetBrains Mono", fontSize: 12, backdropFilter: "blur(20px)" }}
              labelFormatter={() => ""}
              formatter={(v) => [v.toFixed(2), "Close"]}
            />
            {ind?.vwap && <ReferenceLine y={ind.vwap} stroke="rgba(255,255,255,0.5)" strokeDasharray="4 4" label={{ value: "VWAP", position: "right", fill: "rgba(255,255,255,0.6)", fontSize: 10, fontFamily: "JetBrains Mono" }} />}
            {ind?.bb_upper && <ReferenceLine y={ind.bb_upper} stroke="rgba(255,255,255,0.15)" strokeDasharray="2 4" />}
            {ind?.bb_lower && <ReferenceLine y={ind.bb_lower} stroke="rgba(255,255,255,0.15)" strokeDasharray="2 4" />}
            {ind?.ema20 && <ReferenceLine y={ind.ema20} stroke="rgba(108,92,231,0.45)" strokeDasharray="3 3" label={{ value: "EMA20", position: "right", fill: "rgba(108,92,231,0.7)", fontSize: 9, fontFamily: "JetBrains Mono" }} />}
            {ind?.ema50 && <ReferenceLine y={ind.ema50} stroke="rgba(255,209,102,0.4)" strokeDasharray="4 2" label={{ value: "EMA50", position: "right", fill: "rgba(255,209,102,0.7)", fontSize: 9, fontFamily: "JetBrains Mono" }} />}
            <Area type="monotone" dataKey="c" stroke={color} strokeWidth={2} fill={`url(#grad-${ticker})`} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      {ind && (
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-4 pt-4 border-t border-white/5 text-center">
          <Stat label="RSI" value={ind.rsi.toFixed(1)} />
          <Stat label="MACD" value={ind.macd_hist >= 0 ? "Bullish" : "Bearish"} cls={ind.macd_hist >= 0 ? "text-[var(--bull)]" : "text-[var(--bear)]"} />
          <Stat label="VWAP" value={ind.vwap.toFixed(2)} />
          <Stat label="EMA20" value={ind.ema20.toFixed(2)} />
          <Stat label="EMA50" value={ind.ema50.toFixed(2)} />
          <Stat label="RVOL" value={ind.rvol.toFixed(2)} cls={ind.rvol >= 1.5 ? "text-[var(--bull)]" : "text-white"} />
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, cls }) {
  return (
    <div>
      <div className="label-mono">{label}</div>
      <div className={`font-mono text-sm mt-1 tabular-nums ${cls || "text-white"}`}>{value}</div>
    </div>
  );
}
